import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { playersAPI, venuesAPI, matchesAPI, clubsAPI } from '../lib/api';
import api from '../lib/api';
import { Target, Users, MapPin, Play, Filter } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const teamsAPI = {
  getAll: () => api.get('/teams'),
};

export default function NewMatch() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const teamIdParam = searchParams.get('team');
  const clubIdParam = searchParams.get('club');
  const [loading, setLoading] = useState(false);
  const [players, setPlayers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedClub, setSelectedClub] = useState(clubIdParam || 'all');
  const [selectedTeam, setSelectedTeam] = useState(teamIdParam || 'all');
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  
  const [matchType, setMatchType] = useState('singles');
  const [player1Id, setPlayer1Id] = useState('');
  const [player2Id, setPlayer2Id] = useState('');
  const [player1PartnerId, setPlayer1PartnerId] = useState('');
  const [player2PartnerId, setPlayer2PartnerId] = useState('');
  const [venueId, setVenueId] = useState('');
  const [bestOf, setBestOf] = useState('3');
  const [pointsPerGame, setPointsPerGame] = useState('21');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [playersRes, venuesRes, clubsRes, teamsRes] = await Promise.all([
          playersAPI.getAll(),
          venuesAPI.getAll(),
          clubsAPI.getAll(),
          teamsAPI.getAll().catch(() => ({ data: [] })),
        ]);
        setPlayers(playersRes.data || []);
        setFilteredPlayers(playersRes.data || []);
        setVenues(venuesRes.data || []);
        setClubs(clubsRes.data || []);
        setTeams(teamsRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Filter clubs and teams to show only user's clubs/teams
  const userClubs = clubs.filter(c => 
    c.manager_id === user?.user_id || (c.member_ids || []).includes(user?.user_id)
  );
  const userTeams = teams.filter(t => 
    t.created_by === user?.user_id || (t.members || []).some(m => (m.user_id || m.player_id) === user?.user_id)
  );

  useEffect(() => {
    let filtered = players;
    if (selectedTeam !== 'all') {
      const team = teams.find(t => t.team_id === selectedTeam);
      if (team) {
        const memberIds = (team.members || []).map(m => m.user_id || m.player_id);
        filtered = players.filter(p => memberIds.includes(p.user_id));
      }
    } else if (selectedClub !== 'all') {
      const club = clubs.find(c => c.club_id === selectedClub);
      if (club?.member_ids?.length) {
        filtered = players.filter(p => club.member_ids.includes(p.user_id));
      } else {
        filtered = [];
      }
    }
    setFilteredPlayers(filtered);
  }, [selectedClub, selectedTeam, players, clubs, teams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!player1Id || !player2Id) {
      toast.error('Please select both players');
      return;
    }

    if (player1Id === player2Id) {
      toast.error('Players must be different');
      return;
    }

    if (matchType === 'doubles') {
      if (!player1PartnerId || !player2PartnerId) {
        toast.error('Please select partners for doubles match');
        return;
      }
    }

    setLoading(true);
    try {
      const matchData = {
        match_type: matchType,
        player1_id: player1Id,
        player2_id: player2Id,
        player1_partner_id: matchType === 'doubles' ? player1PartnerId : null,
        player2_partner_id: matchType === 'doubles' ? player2PartnerId : null,
        venue_id: venueId && venueId !== 'none' ? venueId : null,
        best_of: parseInt(bestOf),
        points_per_game: parseInt(pointsPerGame),
      };

      const response = await matchesAPI.create(matchData);
      // Auto-start the match so it opens directly in live scoring mode
      try {
        await matchesAPI.start(response.data.match_id);
      } catch (startErr) {
        console.warn('Auto-start failed, match created in scheduled state:', startErr);
      }
      toast.success('Match started! Opening live scoring.');
      navigate(`/matches/${response.data.match_id}/score`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create match');
    } finally {
      setLoading(false);
    }
  };

  const getAvailablePlayers = (excludeIds = []) => {
    return filteredPlayers.filter((p) => !excludeIds.includes(p.user_id));
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="new-match-page">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">New Match</h1>
          <p className="text-muted-foreground mt-1">Set up a new badminton match</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Match Setup
            </CardTitle>
            <CardDescription>Configure match type and players</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Match Type */}
              <div className="space-y-3">
                <Label className="text-base">Match Type</Label>
                <RadioGroup
                  value={matchType}
                  onValueChange={setMatchType}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="singles" id="singles" />
                    <Label htmlFor="singles" className="cursor-pointer">Singles</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="doubles" id="doubles" />
                    <Label htmlFor="doubles" className="cursor-pointer">Doubles</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Club Filter */}
              <div className="space-y-2">
                <Label className="text-base flex items-center gap-2"><Filter className="h-4 w-4" />Filter by Club</Label>
                <Select value={selectedClub} onValueChange={(v) => { setSelectedClub(v); setSelectedTeam('all'); }}>
                  <SelectTrigger data-testid="club-filter-select"><SelectValue placeholder="All players" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Players</SelectItem>
                    {userClubs.map(club => (
                      <SelectItem key={club.club_id} value={club.club_id}>{club.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Team Filter */}
              <div className="space-y-2">
                <Label className="text-base flex items-center gap-2"><Users className="h-4 w-4" />Filter by Team</Label>
                <Select value={selectedTeam} onValueChange={(v) => { setSelectedTeam(v); setSelectedClub('all'); }}>
                  <SelectTrigger data-testid="team-filter-select"><SelectValue placeholder="All players" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Players</SelectItem>
                    {userTeams.map(team => (
                      <SelectItem key={team.team_id} value={team.team_id}>{team.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{filteredPlayers.length} players available</p>
              </div>

              {/* Points per Game */}
              <div className="space-y-3">
                <Label className="text-base">Points / Game</Label>
                <RadioGroup
                  value={pointsPerGame}
                  onValueChange={setPointsPerGame}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="11" id="pts11" />
                    <Label htmlFor="pts11" className="cursor-pointer">11 Points</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="15" id="pts15" />
                    <Label htmlFor="pts15" className="cursor-pointer">15 Points</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="21" id="pts21" />
                    <Label htmlFor="pts21" className="cursor-pointer">21 Points</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Best Of */}
              <div className="space-y-3">
                <Label className="text-base">Best Of</Label>
                <RadioGroup
                  value={bestOf}
                  onValueChange={setBestOf}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="1" id="best1" />
                    <Label htmlFor="best1" className="cursor-pointer">1 Game</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="3" id="best3" />
                    <Label htmlFor="best3" className="cursor-pointer">3 Games</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="5" id="best5" />
                    <Label htmlFor="best5" className="cursor-pointer">5 Games</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Player 1 Selection */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  {matchType === 'singles' ? 'Player 1' : 'Team 1 - Player 1'}
                </Label>
                <Select value={player1Id} onValueChange={setPlayer1Id}>
                  <SelectTrigger data-testid="player1-select">
                    <SelectValue placeholder="Select player" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailablePlayers([player2Id, player1PartnerId, player2PartnerId]).map((player) => (
                      <SelectItem key={player.user_id} value={player.user_id}>
                        {player.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Player 1 Partner (Doubles) */}
              {matchType === 'doubles' && (
                <div className="space-y-3">
                  <Label>Team 1 - Player 2</Label>
                  <Select value={player1PartnerId} onValueChange={setPlayer1PartnerId}>
                    <SelectTrigger data-testid="player1-partner-select">
                      <SelectValue placeholder="Select partner" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailablePlayers([player1Id, player2Id, player2PartnerId]).map((player) => (
                        <SelectItem key={player.user_id} value={player.user_id}>
                          {player.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Player 2 Selection */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  {matchType === 'singles' ? 'Player 2' : 'Team 2 - Player 1'}
                </Label>
                <Select value={player2Id} onValueChange={setPlayer2Id}>
                  <SelectTrigger data-testid="player2-select">
                    <SelectValue placeholder="Select player" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailablePlayers([player1Id, player1PartnerId, player2PartnerId]).map((player) => (
                      <SelectItem key={player.user_id} value={player.user_id}>
                        {player.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Player 2 Partner (Doubles) */}
              {matchType === 'doubles' && (
                <div className="space-y-3">
                  <Label>Team 2 - Player 2</Label>
                  <Select value={player2PartnerId} onValueChange={setPlayer2PartnerId}>
                    <SelectTrigger data-testid="player2-partner-select">
                      <SelectValue placeholder="Select partner" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailablePlayers([player1Id, player1PartnerId, player2Id]).map((player) => (
                        <SelectItem key={player.user_id} value={player.user_id}>
                          {player.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Venue Selection */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Venue (Optional)
                </Label>
                <Select value={venueId} onValueChange={setVenueId}>
                  <SelectTrigger data-testid="venue-select">
                    <SelectValue placeholder="Select venue" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No venue</SelectItem>
                    {venues.map((venue) => (
                      <SelectItem key={venue.venue_id} value={venue.venue_id}>
                        {venue.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                className="w-full bg-secondary hover:bg-secondary/90 font-bold"
                disabled={loading}
                data-testid="create-match-btn"
              >
                <Play className="mr-2 h-5 w-5" />
                {loading ? 'Creating...' : 'Create Match & Start Scoring'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
